import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import PercentIcon from "@mui/icons-material/Percent";
import Slider from "@mui/material/Slider";
import { Helmet } from "react-helmet";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { IconButton, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import PDFGenerator from "../pdf/pdf-step-up-sip";
import ReactGA from "react-ga4";
import { saveStepUpSIP, updateStepUpSIP } from "../Database/stepup-sip";
import BootstrapTooltip from "../components/tooltip";
import { StepUpSIP } from "../models/stepup-sip";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrencySymbol } from "../Redux/currencySlice";
import SavedSnackbar from "../utils/saved-snackbar";

const StepUpSIPCalculator: React.FC<{ stepUpSIP?: StepUpSIP }> = ({ }) => {
  const location = useLocation();

  const [stepUpSIPData, setStepUpSIP] = useState<StepUpSIP | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const [monthlyDeposit, setMonthlyDeposit] = useState("10,000");
  const [stepUpPercent, setStepUpPercent] = useState("10");
  const [interestRate, setInterestRate] = useState("7.5");
  const [timePeriod, setTimePeriod] = useState("5");
  const [monthlyDepositAmount, setMonthlyDepositAmount] = useState<number | null>(null);

  const [principalAmount, setPrincipalAmount] = useState<number | null>(null);
  const [estimatedReturns, setEstimatedReturns] = useState<number | null>(null);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [investmentData, setInvestmentData] = useState<
    { year: number; totalInvestment: number; totalReturns: number }[]
  >([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const [rawMonthlyDeposit, setRawMonthlyDeposit] = useState<string>("");
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [errors, setErrors] = useState({
    monthlyDeposit: "",
    stepUpPercent: "",
    interestRate: "",
    timePeriod: "",
  });
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [shouldCalculate, setShouldCalculate] = useState(false);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);

  const formatNumberWithCommas = (value: number | string) => {
    return new Intl.NumberFormat("en-IN").format(Number(value));
  };

  const validateInputs = () => {
    const newErrors = {
      monthlyDeposit: "",
      stepUpPercent: "",
      interestRate: "",
      timePeriod: "",
    };
    let isValid = true;

    const minDepositAmount = 500;

    if (!monthlyDeposit || isNaN(parseFloat(monthlyDeposit.replace(/,/g, "")))) {
      newErrors.monthlyDeposit = "Monthly deposit amount is mandatory.";
      isValid = false;
    } else {
      const depositValue = parseFloat(monthlyDeposit.replace(/,/g, ""));
      if (depositValue < minDepositAmount || depositValue > 1000000) {
        newErrors.monthlyDeposit = `Deposit amount must be between ${currencySymbol}${minDepositAmount} and ${currencySymbol}1000000.`;
        isValid = false;
      }
    }

    const stepUpValue = parseFloat(stepUpPercent);
    if (!stepUpPercent || isNaN(stepUpValue) || stepUpValue < 1 || stepUpValue > 50) {
      newErrors.stepUpPercent = "Annual step-up percentage must be between 1 and 50.";
      isValid = false;
    }

    const interestRateValue = parseFloat(interestRate);
    if (!interestRate || isNaN(interestRateValue) || interestRateValue < 1 || interestRateValue > 30) {
      newErrors.interestRate = "Expected interest rate must be between -1 and 30.";
      isValid = false;
    }


    if (!timePeriod || isNaN(parseFloat(timePeriod)) || parseFloat(timePeriod) > 50) {
      newErrors.timePeriod = "Time period in years is mandatory and can't exceed 50 years.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };


  const calculateResults = () => {
    ReactGA.event({
      category: "Step-up-SIP calculator",
      action: "Step-up-SIP Calculate Button",
      label: "Step-up-SIP Calculator",
    });
    if (!validateInputs()) {
      return;
    }

    const depositValue = parseFloat(monthlyDeposit.replace(/,/g, ""));
    const stepUpValue = parseFloat(stepUpPercent) / 100;
    const rateValue = parseFloat(interestRate) / 100;
    const periodValue = parseFloat(timePeriod);

    const totalMonths = periodValue * 12;
    const monthlyRate = rateValue / 12;
    let totalPrincipal = 0;
    let maturityValue = 0;
    let investmentDataTemp = [];

    for (let month = 1; month <= totalMonths; month++) {
      const yearsElapsed = Math.floor((month - 1) / 12);
      const currentDeposit =
        depositValue * Math.pow(1 + stepUpValue, yearsElapsed);

      totalPrincipal += currentDeposit;

      const futureValueCurrentDeposit =
        currentDeposit * Math.pow(1 + monthlyRate, totalMonths - month + 1);
      maturityValue += futureValueCurrentDeposit;

      if (month % 12 === 0) {
        const year = month / 12;
        const totalReturns = maturityValue - totalPrincipal;
        investmentDataTemp.push({
          year,
          totalInvestment: totalPrincipal,
          totalReturns: totalReturns,
          maturityValue: maturityValue,
        });
      }
    }

    const totalReturns = maturityValue - totalPrincipal;

    setPrincipalAmount(totalPrincipal);
    setEstimatedReturns(totalReturns);
    setTotalAmount(maturityValue);
    setInvestmentData(investmentDataTemp);
    setShowPdfButton(true);
    setMonthlyDepositAmount(depositValue);
    setIsCalculated(true)
    
  };

  useEffect(() => {
    const { stepSip: passedStepSip, currencySymbol: passedCurrencySymbol } =
      location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    }

    if (passedStepSip) {
      setStepUpSIP(passedStepSip);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setMonthlyDeposit(formatNumberWithCommas(passedStepSip.monthlyDeposit));
      setStepUpPercent(passedStepSip.stepUpPercent.toString());
      setInterestRate(passedStepSip.interestRate.toString());
      setTimePeriod(passedStepSip.timePeriod.toString());
      setTitle(passedStepSip.title);
      setSavedTitle(passedStepSip.title || "");

    }
    setShouldCalculate(true);

  }, [location.state, stepUpSIPData]);

  useEffect(() => {
    if (shouldCalculate) {
      calculateResults();
      setShouldCalculate(false);
    }
  }, [shouldCalculate]);

  const calculateStepUp = () => {
    calculateResults();
  };

  const handleReset = () => {
    ReactGA.event({
      category: "Step-up-SIP calculator",
      action: "Step-up-SIP Reset Button",
      label: "Step-up-SIP Calculator",
    });
    setMonthlyDeposit("10,000");
    setStepUpPercent("10");
    setInterestRate("7.5");
    setTimePeriod("5");
    setPrincipalAmount(null);
    setEstimatedReturns(null);
    setTotalAmount(null);
    setInvestmentData([]);
    setShowPdfButton(false);
    setErrors({
      monthlyDeposit: "",
      stepUpPercent: "",
      interestRate: "",
      timePeriod: "",
    });
  };
  // useEffect(() => {
  //   if (monthlyDeposit && stepUpPercent && interestRate && timePeriod) {
  //     handleCalculate();
  //   }
  // }, [monthlyDeposit, stepUpPercent, interestRate, timePeriod]);

  const handleMonthlyDepositChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value === "") {
      setMonthlyDeposit("");
      setRawMonthlyDeposit("");
      return;
    }
    let numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue === "") {
      setMonthlyDeposit("");
      setRawMonthlyDeposit("");
      return;
    }

    const maxLimit = 1000000;
    if (parseInt(numericValue, 10) <= 0) {
      setMonthlyDeposit("");
      setRawMonthlyDeposit("");
      return;
    }

    if (parseInt(numericValue, 10) > maxLimit) {
      numericValue = maxLimit.toString();
    }

    setRawMonthlyDeposit(numericValue);

    const formattedValue = formatNumberWithCommas(numericValue);
    setMonthlyDeposit(formattedValue);
  };


  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    isDecimalAllowed: boolean = false,
    max?: number,
    decimalPlaces: number = 2
  ) => {
    let sanitizedValue = value;

    if (sanitizedValue === "") {
      setter("");
      return;
    }

    if (isDecimalAllowed) {
      sanitizedValue = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*)\..*/g, "$1");

      const parts = sanitizedValue.split(".");
      if (parts.length > 1 && parts[1].length > decimalPlaces) {
        parts[1] = parts[1].slice(0, decimalPlaces);
        sanitizedValue = parts.join(".");
      }
    } else {
      sanitizedValue = value.replace(/[^0-9]/g, "").slice(0, 2);
    }

    if (parseFloat(sanitizedValue) <= 0) {
      setter("");
      return;
    }

    if (max !== undefined && parseFloat(sanitizedValue) > max) {
      return;
    }

    setter(sanitizedValue);
  };

  const handleNumberChange =
    (setter: React.Dispatch<React.SetStateAction<string>>, max?: number) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const validValue = value.replace(/[^0-9]/g, "").slice(0, 2);
        if ((max !== undefined && parseFloat(validValue) > max) || (validValue === "0" && validValue.length === 1)) {
          return;
        }
        setter(validValue);
      };

  const handleSubmit = async () => {
    if (title.trim().length > 50) {
      alert("Title must be 50 characters or less.");
      return;
    }
    if (!validateInputs()) {
      return;
    }

    const stepUpSIP: StepUpSIP = {
      monthlyDeposit: parseFloat(monthlyDeposit.replace(/,/g, "")),
      stepUpPercent: parseFloat(stepUpPercent),
      interestRate: parseFloat(interestRate),
      timePeriod: parseInt(timePeriod?.toString() || "0", 10),
      totalPrincipal: principalAmount ?? 0,
      estimatedReturns: estimatedReturns ?? 0,
      totalAmount: totalAmount ?? 0,
      currencySymbol,
      title: title,
    };

    if (stepUpSIPData?.id) {
      await updateStepUpSIP(stepUpSIPData.id, stepUpSIP);
      console.log(`stepUpSIP with ID ${stepUpSIP.id} updated successfully.`);
    } else {
      await saveStepUpSIP(stepUpSIP);
      console.log("New stepUpSIP created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };



  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const lineChartData = {
    labels: investmentData.map((data) => `Year ${data.year}`),
    datasets: [
      {
        label: "Total Investment (Rs)",
        data: investmentData.map((data) => data.totalInvestment),
        borderColor: "#4CAF50",
        fill: false,
        tension: 0.1,
      },
      {
        label: "Estimated Returns (Rs)",
        data: investmentData.map((data) => data.totalReturns),
        borderColor: "#FF9800",
        fill: false,
        tension: 0.1,
      },
    ],
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      calculateStepUp();
    }
  };


  return (
    <div className="container mt-5" onKeyPress={handleKeyPress}>
      <div className="d-flex justify-content-between align-items-center">
        <h5
          className="mb-2"
          style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}
        >
          Step Up SIP Calculator
        </h5>
      </div>

      <p>
        A Step-Up SIP Calculator helps estimate the future value of systematic
        investments that increase periodically, allowing you to enhance your
        investment potential over time while considering the expected rate of
        return.
      </p>

      <Helmet>
        <title>Step-Up SIP Calculator</title>
        <meta
          name="description"
          content="Plan your investments with our Step-Up SIP Calculator. Calculate your future savings by adjusting your monthly SIP amounts over time."
        />
        <link rel="canonical" href="/step-up-sip-calculator.html" />
      </Helmet>
      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
      <div className="row justify-content-center">
        {savedTitle && (
          <Typography variant="h6" style={{ marginLeft: '20px' }}>
            {savedTitle}
          </Typography>
        )}
        <div className="col-12 col-md-6 mb-4">

          <div
            className="card shadow-sm"
            style={{ width: "100%", height: "600px", margin: "0 auto", overflow: 'auto' }}
          >
            <div className="card-body">
              <div className="mb-3">
                <label htmlFor="monthlyDeposit" className="form-label">
                  Monthly Deposit Amount ({currencySymbol})
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.monthlyDeposit ? "is-invalid" : ""
                    }`}
                  id="monthlyDeposit"
                  value={monthlyDeposit}
                  onChange={handleMonthlyDepositChange}
                />
                {errors.monthlyDeposit && (
                  <div className="invalid-feedback">
                    {errors.monthlyDeposit}
                  </div>
                )}
              </div>
              <div className="mb-3 d-flex justify-content-center">
                <Slider
                  min={500}
                  max={1000000}
                  step={1000}
                  value={parseFloat(monthlyDeposit.replace(/,/g, "")) || 0}
                  onChange={(e, newValue) =>
                    setMonthlyDeposit(newValue.toString())
                  }
                  valueLabelDisplay="auto"
                  shiftStep={30}
                  sx={{ width: "95%" }}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="stepUpPercent" className="form-label">
                  Annual Step-Up (%)
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`form-control ${errors.stepUpPercent ? "is-invalid" : ""
                      }`}
                    id="stepUpPercent"
                    value={stepUpPercent}
                    onChange={(e) =>
                      handleInputChange(
                        setStepUpPercent,
                        e.target.value,
                        true,
                        50,
                        2
                      )
                    }
                  />
                  <span className="input-group-text">
                    <PercentIcon />
                  </span>
                  {errors.stepUpPercent && (
                    <div className="invalid-feedback">
                      {errors.stepUpPercent}
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="interestRate" className="form-label">
                  Interest Rate (%)
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`form-control ${errors.interestRate ? "is-invalid" : ""
                      }`}
                    id="interestRate"
                    value={interestRate}
                    onChange={(e) =>
                      handleInputChange(
                        setInterestRate,
                        e.target.value,
                        true,
                        30,
                        2
                      )
                    }
                  />
                  <span className="input-group-text">
                    <PercentIcon />
                  </span>
                  {errors.interestRate && (
                    <div className="invalid-feedback">
                      {errors.interestRate}
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="timePeriod" className="form-label">
                  Time Period (Years)
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.timePeriod ? "is-invalid" : ""
                    }`}
                  id="timePeriod"
                  value={timePeriod}
                  onChange={(e) => handleNumberChange(setTimePeriod, 50)(e)}
                />
                {errors.timePeriod && (
                  <div className="invalid-feedback">{errors.timePeriod}</div>
                )}
              </div>

              <div
                className="d-flex justify-content-center "
                style={{ marginTop: "60px" }}
              >
                <button
                  className="btn btn-primary btn-lg "
                  onClick={calculateStepUp}
                >
                  Calculate
                </button>
              </div>
              <div className="d-flex justify-content-center">
                <a
                  onClick={handleReset}
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  className=" pt-2"
                >
                  Reset
                </a>
              </div>
            </div>
          </div>
        </div>
        {principalAmount !== null &&
          estimatedReturns !== null &&
          totalAmount !== null && (
            <div className="col-12 col-md-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title ">Result</h5>
                    <BootstrapTooltip title="Save Calculation" placement="top">
                      <IconButton color="secondary" onClick={handleDialogOpen}>
                        {icon}
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <p className="card-text">
                    <strong>Monthly Deposit Amount:</strong>
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {monthlyDepositAmount !== null
                        ? formatNumberWithCommas(Math.ceil(Number(monthlyDepositAmount.toFixed(2))))
                        : '0'}
                    </span>
                  </p>
                  <p>
                    <strong>Total Principal Invested :</strong>{" "}
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {principalAmount !== null
                        ? formatNumberWithCommas(Math.ceil(principalAmount))
                        : "---"}
                    </span>
                  </p>

                  <p>
                    <strong>Estimated Returns : </strong>
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {estimatedReturns !== null
                        ? formatNumberWithCommas(Math.ceil(estimatedReturns))
                        : "---"}
                    </span>
                  </p>
                  <p>
                    <strong>Total Amount : </strong>{" "}
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {totalAmount !== null
                        ? formatNumberWithCommas(Math.ceil(totalAmount))
                        : "---"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="card shadow-sm mt-4 mb-3">
                <div className="card-body">
                  <h5 className="card-title mb-4">
                    Investment Growth Over Time
                  </h5>
                  {investmentData.length > 0 ? (
                    <Line
                      ref={chartRef}
                      data={lineChartData}
                      options={{ responsive: true }}
                    />
                  ) : (
                    <p>No data available</p>
                  )}
                </div>
              </div>
            </div>
          )}
        <div
          className="d-flex justify-content-end"
          style={{ marginTop: "50px", paddingLeft: "90px" }}
        >
          {showPdfButton && (
            <PDFGenerator
              yearlyData={investmentData}
              monthlyDeposit={monthlyDeposit}
              stepUpPercent={stepUpPercent}
              interestRate={interestRate}
              timePeriod={timePeriod}
              isCalculated={isCalculated}
            />
          )}
        </div>
      </div>
      <SaveTitleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={title}
        setTitle={setTitle}
        onSubmit={handleSubmit}
        onCancel={handleCancel}

      />
    </div>
  );
};

export default StepUpSIPCalculator;
